import { Logger } from "./Logger.js";

/**
 * Utility class for handling camera position capture and restoration
 */
export class CameraUtils {
	constructor(viewer) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("CameraUtils");
	}

	/**
	 * Capture the current camera position and state
	 * @returns {Object|null} Camera state object or null if failed
	 */
	captureCamera() {
		try {
			if (!this.viewer) {
				this.logger.error("Viewer not available for camera capture");
				return null;
			}

			// Get camera state using Forge Viewer API
			const camera = this.viewer.getCamera();
			const state = this.viewer.getState();

			// Extract essential camera information
			const cameraData = {
				// Camera position and orientation
				position: {
					x: camera.position.x,
					y: camera.position.y,
					z: camera.position.z,
				},
				target: {
					x: camera.target.x,
					y: camera.target.y,
					z: camera.target.z,
				},
				up: {
					x: camera.up.x,
					y: camera.up.y,
					z: camera.up.z,
				},
				fov: camera.fov,
				aspect: camera.aspect,

				// Viewer state for more comprehensive restoration
				viewerState: {
					viewport: state.viewport || null,
					objectTree: state.objectTree || null,
					cutPlanes: state.cutPlanes || null,
					renderOptions: state.renderOptions || null,
				},

				// Metadata
				timestamp: Date.now(),
				viewType: this.viewer.model?.is2d() ? "2D" : "3D",
			};

			this.logger.debug("Camera captured:", {
				position: cameraData.position,
				target: cameraData.target,
				viewType: cameraData.viewType,
			});

			return cameraData;
		} catch (error) {
			this.logger.error("Error capturing camera:", error);
			return null;
		}
	}

	/**
	 * Restore camera to a previously captured position
	 * @param {Object} cameraData - Camera data from captureCamera()
	 * @param {Object} options - Restoration options
	 * @param {boolean} options.animate - Whether to animate the transition
	 * @param {number} options.duration - Animation duration in milliseconds
	 * @returns {Promise<boolean>} True if restoration was successful
	 */
	async restoreCamera(cameraData, options = {}) {
		try {
			if (!this.viewer) {
				this.logger.error("Viewer not available for camera restoration");
				return false;
			}

			if (!cameraData) {
				this.logger.error("No camera data provided for restoration");
				return false;
			}

			const { animate = true, duration = 1000 } = options;

			this.logger.debug("Restoring camera to:", {
				position: cameraData.position,
				target: cameraData.target,
				viewType: cameraData.viewType,
			});

			// Method 1: Use restoreState if viewer state is available
			if (cameraData.viewerState && cameraData.viewerState.viewport) {
				try {
					await this.restoreViewerState(cameraData, animate, duration);
					return true;
				} catch (error) {
					this.logger.warn(
						"Failed to restore using viewer state, falling back to camera API",
						error
					);
				}
			}

			// Method 2: Fallback to direct camera manipulation
			return await this.restoreCameraPosition(cameraData, animate, duration);
		} catch (error) {
			this.logger.error("Error restoring camera:", error);
			return false;
		}
	}

	/**
	 * Restore camera using viewer state (more comprehensive)
	 * @param {Object} cameraData - Camera data
	 * @param {boolean} animate - Whether to animate
	 * @param {number} duration - Animation duration
	 * @returns {Promise<void>}
	 */
	async restoreViewerState(cameraData, animate, duration) {
		return new Promise((resolve, reject) => {
			try {
				// Prepare state object for restoration
				const stateToRestore = {
					...cameraData.viewerState,
					viewport: cameraData.viewerState.viewport,
				};

				if (animate) {
					// Animated restoration using viewer's built-in animation
					this.viewer.restoreState(stateToRestore, null, false);
					setTimeout(resolve, duration);
				} else {
					// Immediate restoration
					this.viewer.restoreState(stateToRestore);
					resolve();
				}
			} catch (error) {
				reject(error);
			}
		});
	}

	/**
	 * Restore camera using direct camera position manipulation
	 * @param {Object} cameraData - Camera data
	 * @param {boolean} animate - Whether to animate
	 * @param {number} duration - Animation duration
	 * @returns {Promise<boolean>}
	 */
	async restoreCameraPosition(cameraData, animate, duration) {
		return new Promise((resolve) => {
			try {
				const { position, target, up } = cameraData;

				if (animate) {
					// Animated camera transition
					this.viewer.navigation.setView(position, target, up, duration);
					setTimeout(() => resolve(true), duration);
				} else {
					// Immediate camera positioning
					const camera = this.viewer.getCamera();
					camera.position.set(position.x, position.y, position.z);
					camera.target.set(target.x, target.y, target.z);
					camera.up.set(up.x, up.y, up.z);

					// Apply FOV if available
					if (cameraData.fov) {
						camera.fov = cameraData.fov;
					}

					this.viewer.impl.syncCamera();
					this.viewer.impl.invalidate(true);

					resolve(true);
				}
			} catch (error) {
				this.logger.error("Error in direct camera positioning:", error);
				resolve(false);
			}
		});
	}

	/**
	 * Check if camera data is valid
	 * @param {Object} cameraData - Camera data to validate
	 * @returns {boolean} True if valid
	 */
	isValidCameraData(cameraData) {
		if (!cameraData) return false;

		const requiredFields = ["position", "target", "up"];
		return requiredFields.every(
			(field) =>
				cameraData[field] &&
				typeof cameraData[field].x === "number" &&
				typeof cameraData[field].y === "number" &&
				typeof cameraData[field].z === "number"
		);
	}

	/**
	 * Get camera distance from target
	 * @param {Object} cameraData - Camera data
	 * @returns {number} Distance from camera to target
	 */
	getCameraDistance(cameraData) {
		if (!this.isValidCameraData(cameraData)) return 0;

		const { position, target } = cameraData;
		const dx = position.x - target.x;
		const dy = position.y - target.y;
		const dz = position.z - target.z;

		return Math.sqrt(dx * dx + dy * dy + dz * dz);
	}

	/**
	 * Compare two camera positions to see if they're similar
	 * @param {Object} camera1 - First camera data
	 * @param {Object} camera2 - Second camera data
	 * @param {number} tolerance - Position tolerance
	 * @returns {boolean} True if cameras are similar
	 */
	areCamerasSimilar(camera1, camera2, tolerance = 0.1) {
		if (!this.isValidCameraData(camera1) || !this.isValidCameraData(camera2)) {
			return false;
		}

		const fields = ["position", "target"];
		return fields.every((field) => {
			const c1 = camera1[field];
			const c2 = camera2[field];
			return (
				Math.abs(c1.x - c2.x) < tolerance &&
				Math.abs(c1.y - c2.y) < tolerance &&
				Math.abs(c1.z - c2.z) < tolerance
			);
		});
	}

	/**
	 * Get human-readable camera info for debugging
	 * @param {Object} cameraData - Camera data
	 * @returns {string} Formatted camera information
	 */
	getCameraInfo(cameraData) {
		if (!this.isValidCameraData(cameraData)) {
			return "Invalid camera data";
		}

		const { position, target, viewType } = cameraData;
		const distance = this.getCameraDistance(cameraData);

		return (
			`${viewType || "3D"} Camera - Pos: (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}), ` +
			`Target: (${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}), ` +
			`Distance: ${distance.toFixed(2)}`
		);
	}
}
